import type { GoodLuckCoreDTOsMetricAvailability } from "@/shared/api/generated-kubb/types/goodLuck/core/DTOs/MetricAvailability";
import type { GoodLuckCoreDTOsReplayMetricValueDto } from "@/shared/api/generated-kubb/types/goodLuck/core/DTOs/ReplayMetricValueDto";
import type { GoodLuckCoreDTOsReplayGameScoreDto } from "@/shared/api/generated-kubb/types/goodLuck/core/DTOs/ReplayGameScoreDto";
import type { GoodLuckCoreDTOsReplayPlayerMetricsDto } from "@/shared/api/generated-kubb/types/goodLuck/core/DTOs/ReplayPlayerMetricsDto";

/**
 * KAN-727: доменные типы поведенческих метрик (эпик KAN-719).
 *
 * Раньше здесь лежал контракт, написанный руками, потому что тестовый стенд лежал и
 * `make api-sync-test` был недоступен. Синхронизация прошла, и теперь **имена и состав
 * полей берутся из сгенерированного** — дублировать контракт больше негде.
 *
 * Но файл не исчез, и вот почему: генерация описывает контракт СЛАБЕЕ, чем он есть на
 * самом деле, в двух местах.
 *
 * 1. `key` приходит как `string | null`. Генерация не может знать, что ключей ровно семь
 *    и они фиксированы — в swagger это просто строка. Здесь юнион восстанавливается,
 *    иначе опечатка в `t(\`axis.${key}\`)` не будет поймана компилятором.
 *
 * 2. ВСЕ поля помечены необязательными. Это не свойство бэкенда, а артефакт спеки:
 *    контроллеры не описывают `required`, и Kubb честно делает всё опциональным. Бэкенд
 *    при этом всегда шлёт полный объект — `ReplayMetricValueDto` не имеет ни одного
 *    nullable-поля кроме `value`, `percentile` и `peerGroupRole`, где null осмыслен.
 *
 * Правильное лечение второго пункта — на бэкенде, атрибутами на контроллерах, чтобы
 * спека помечала required и генерация давала точные типы сама. Это отдельная задача и
 * общая болезнь проекта (см. `ai/known-issues.md`, раздел про `Errors: any`). Пока её
 * нет, сужение живёт здесь — в одном месте, а не россыпью `?.` по шести витринам.
 *
 * ЧТО ЗДЕСЬ НЕЛЬЗЯ ДЕЛАТЬ: расширять список полей руками. Появилось новое поле в DTO —
 * оно приезжает через `make api-sync-test`, а не дописывается сюда.
 */

/**
 * Насколько метрике можно верить. Совпадает со сгенерированным один в один: бэкенд
 * сериализует enum строками, и swagger описывает все четыре состояния.
 *
 * Состояния именно четыре, и схлопывать их в интерфейсе нельзя:
 *  - `Available`   — исходные поля есть во всех учтённых играх;
 *  - `Partial`     — есть в части игр, метрика посчитана по ним;
 *  - `Unavailable` — полей нет ни в одной игре (в патче их не было);
 *  - `Undefined`   — данные есть, но метрика не определена: знаменатель ноль.
 *
 * Последние два дают одинаково пустую ось на радаре, но объясняются пользователю
 * по-разному: «данных нет» против «делить не на что».
 */
export type MetricAvailability = GoodLuckCoreDTOsMetricAvailability;

/**
 * СЕМЬ ПОВЕДЕНЧЕСКИХ ОСЕЙ — те, из которых строится радар (KAN-724).
 *
 * Это НЕ весь список метрик, и разделение здесь принципиальное. Радар читается как
 * фигура: семь осей складываются в силуэт, который человек сравнивает с другим
 * силуэтом. Двадцать семь осей силуэта не дают — получается круг, по которому
 * ничего не видно. Поэтому список осей отдельный и растёт только осознанно.
 */
export const BEHAVIOR_METRIC_KEYS = [
  "shotcalling",
  "objectiveFocus",
  "towerDiveAggression",
  "visionQuality",
  "goldUtilization",
  "bountyEfficiency",
  "ccPressure",
] as const;

/**
 * ВСЕ ключи метрик, которые отдаёт бэкенд. Генерация их не знает — в спеке это
 * `string`, — поэтому список живёт здесь и синхронизируется с `ReplayMetricKeys`
 * вручную.
 *
 * KAN-814: было семь, стало двадцать семь. Метрики второй волны (KAN-758..760)
 * приезжали в ответе, но во фронтовом типе их не существовало — обратиться к
 * `takedownsBefore15` было нельзя, компилятор отвергал ключ, которого нет в union.
 * Витрины ранней игры и профиля урона упирались именно в это.
 */
export const REPLAY_METRIC_KEYS = [
  ...BEHAVIOR_METRIC_KEYS,

  // KAN-758: профиль урона.
  "damagePhysicalShare",
  "damageMagicShare",
  "damageTrueShare",
  "teamDamageShare",
  "damagePerMinute",
  "damagePerGold",
  "tankLoad",
  "teamDamageTakenShare",
  "supportLoad",

  // KAN-759: участие в игре.
  "killParticipation",
  "deathTimeShare",
  "longestTimeAlive",
  "ccTimeEffective",
  "ccTimeTotal",

  // KAN-760: карта и ранняя игра.
  "takedownsBefore15",
  "turretPlates",
  "goldFromPlates",
  "goldFromStructures",
  "damageToStructures",
  "counterJungleShare",
] as const;

export type ReplayMetricKey = (typeof REPLAY_METRIC_KEYS)[number];

/** Ось радара — подмножество ключей, а не любой ключ. */
export type BehaviorMetricKey = (typeof BEHAVIOR_METRIC_KEYS)[number];

/**
 * Метрика с сужением: обязательные поля обязательны, ключ — из перечисленных.
 * Состав полей целиком из сгенерированного типа, здесь только уточнения.
 */
export type ReplayMetricValue = Omit<
  Required<GoodLuckCoreDTOsReplayMetricValueDto>,
  "key"
> & {
  key: ReplayMetricKey;
};

/**
 * Метрики игрока. `metrics` у сгенерированного типа может быть null — здесь массив
 * обязателен: бэкенд всегда отдаёт семь метрик, а пустой ответ означал бы отсутствие
 * игрока, и его отсекают на уровне запроса (404).
 */
export type ReplayPlayerMetrics = Omit<
  Required<GoodLuckCoreDTOsReplayPlayerMetricsDto>,
  "metrics"
> & {
  metrics: ReplayMetricValue[];
};

/**
 * KAN-1166: строка «игрок × игра» лёгкой ручки `/replay-metrics/game-scores` —
 * только поля столбика профиля. Тип сгенерирован Kubb из ReplayGameScoreDto;
 * `integrityFlags` бэкенд всегда отдаёт массивом (`= new()`), здесь он обязателен.
 */
export type ReplayGameScore = Omit<Required<GoodLuckCoreDTOsReplayGameScoreDto>, "integrityFlags"> & {
  integrityFlags: string[];
};

/** Метрика есть и её можно показать числом. */
export function hasValue(metric: ReplayMetricValue): boolean {
  return metric.value !== null;
}

/** Перцентиль посчитан — ось радара можно строить. */
export function hasPercentile(metric: ReplayMetricValue): boolean {
  return metric.percentile !== null;
}
